import { useState } from 'react';

import type { CapabilityMap, RoleResponse } from '../../lib/api-client';
import { useRoleSkillAccess } from '../../hooks/use-role-skill-access';

import { SkillAccessEditor } from './SkillAccessEditor';

const CAPABILITIES: { key: keyof CapabilityMap; label: string; help: string }[] = [
  { key: 'full_access', label: 'Full access', help: 'Superuser — every capability.' },
  { key: 'manage_users', label: 'Manage users', help: 'Create users and assign roles.' },
  { key: 'manage_roles', label: 'Manage roles', help: 'Create/edit/delete roles and their skills.' },
  { key: 'creator_mode', label: 'Creator Mode', help: 'Author skills (RW repo); sees all skills.' },
  { key: 'sftp_access', label: 'SFTP access', help: 'May log in to the workspace SFTP server; sees the tenants of their own organization tree.' },
];

interface Props {
  /** null = creating a new role. */
  role: RoleResponse | null;
  onClose: () => void;
  onCreate: (body: { name: string; slug: string; description?: string; capabilities: CapabilityMap }) => Promise<void>;
  onUpdate: (id: string, body: { name?: string; description?: string; capabilities?: CapabilityMap }) => Promise<void>;
  onDelete?: ((id: string) => Promise<void>) | undefined;
}

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export function RoleEditor({ role, onClose, onCreate, onUpdate, onDelete }: Props) {
  const isNew = role === null;
  const isSystem = role?.is_system ?? false;
  const [name, setName] = useState(role?.name ?? '');
  const [slug, setSlug] = useState(role?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [description, setDescription] = useState(role?.description ?? '');
  const [capabilities, setCapabilities] = useState<CapabilityMap>(role?.capabilities ?? {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const creatorOrFull = capabilities.creator_mode === true || capabilities.full_access === true;

  // Role skill access (only meaningful for an existing role).
  const {
    rows: skillRows,
    status: skillStatus,
    saveStatus: skillSaveStatus,
    error: skillError,
    save: saveSkillAccess,
  } = useRoleSkillAccess(role?.id ?? null, { enabled: !isNew });

  const toggleCap = (key: keyof CapabilityMap) => {
    if (isSystem) return;
    setCapabilities((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (busy || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        await onCreate({
          name: name.trim(),
          slug: slug || slugify(name),
          description: description.trim() || undefined,
          capabilities,
        });
      } else {
        await onUpdate(role!.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          // System role capabilities are locked server-side; don't send them.
          ...(isSystem ? {} : { capabilities }),
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save role');
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 12,
          padding: '18px 20px 20px',
          width: 480,
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 40px)',
          overflowY: 'auto',
          boxShadow: 'var(--shadow)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            {isNew ? 'New role' : role!.name}
          </div>
          {isSystem && <Badge>system</Badge>}
        </div>

        <Label>Name</Label>
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          disabled={isSystem || busy}
          placeholder="Customer Support"
          style={inputStyle(isSystem)}
        />

        <div style={{ height: 12 }} />
        <Label>Slug</Label>
        <input
          type="text"
          value={slug}
          onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
          disabled={!isNew || busy}
          placeholder="customer-support"
          style={inputStyle(!isNew)}
        />

        <div style={{ height: 12 }} />
        <Label>Description</Label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSystem || busy}
          placeholder="What this role is for"
          style={inputStyle(isSystem)}
        />

        <div style={{ height: 14 }} />
        <Label>Capabilities</Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {CAPABILITIES.map((c) => (
            <label
              key={c.key}
              title={c.help}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                padding: '6px 8px',
                borderRadius: 4,
                cursor: isSystem ? 'default' : 'pointer',
                opacity: isSystem ? 0.7 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={capabilities[c.key] === true}
                disabled={isSystem || busy}
                onChange={() => toggleCap(c.key)}
                style={{ marginTop: 2, accentColor: 'var(--accent)' }}
              />
              <span style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13, color: 'var(--text)' }}>{c.label}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-soft)' }}>{c.help}</span>
              </span>
            </label>
          ))}
        </div>
        {isSystem && (
          <div style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 4 }}>
            System role capabilities are locked.
          </div>
        )}

        {/* Skill access — only for an existing role. */}
        {!isNew && (
          <>
            <div style={{ height: 14 }} />
            <Label>Skill access</Label>
            <SkillAccessEditor
              rows={skillRows}
              status={skillStatus}
              saveStatus={skillSaveStatus}
              error={skillError}
              onSave={saveSkillAccess}
              editable={!creatorOrFull}
              disabledNote="This role can use Creator Mode and has access to all skills — per-skill grants don't apply."
              disabled={busy}
            />
          </>
        )}

        {error && (
          <div role="alert" style={errorStyle}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'space-between' }}>
          <div>
            {!isNew && !isSystem && onDelete && !confirmingDelete && (
              <button type="button" onClick={() => setConfirmingDelete(true)} disabled={busy} style={dangerStyle}>
                Delete
              </button>
            )}
            {confirmingDelete && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                <span style={{ color: 'var(--kind-failure)' }}>Delete role?</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await onDelete!(role!.id);
                      onClose();
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to delete role');
                      setBusy(false);
                      setConfirmingDelete(false);
                    }
                  }}
                  style={{ ...dangerStyle, background: 'var(--kind-failure)', color: '#fff', border: 0 }}
                >
                  Yes, delete
                </button>
                <button type="button" onClick={() => setConfirmingDelete(false)} disabled={busy} style={secondaryStyle}>
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={busy} style={secondaryStyle}>
              Close
            </button>
            {!isSystem && (
              <button
                type="button"
                onClick={handleSave}
                disabled={busy || !name.trim()}
                data-testid="role-save"
                style={{
                  padding: '7px 14px',
                  fontSize: 12,
                  fontWeight: 500,
                  background: 'var(--accent)',
                  color: '#fff',
                  border: 0,
                  borderRadius: 6,
                  cursor: busy || !name.trim() ? 'not-allowed' : 'pointer',
                  opacity: busy || !name.trim() ? 0.6 : 1,
                  fontFamily: 'inherit',
                }}
              >
                {busy ? 'Saving…' : isNew ? 'Create role' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 20,
      }}
    >
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 10, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 5 }}>
      {children}
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontSize: 10, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: 0.4, background: 'var(--bg-alt)', padding: '2px 7px', borderRadius: 999, fontWeight: 500 }}>
      {children}
    </span>
  );
}

function inputStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '8px 10px',
    fontSize: 13,
    fontFamily: 'inherit',
    background: 'var(--bg)',
    border: '1px solid var(--border-strong)',
    borderRadius: 6,
    color: 'var(--text)',
    outline: 'none',
    opacity: disabled ? 0.6 : 1,
  };
}

const errorStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '6px 10px',
  background: 'rgba(192,57,43,0.06)',
  border: '1px solid rgba(192,57,43,0.18)',
  borderRadius: 4,
  fontSize: 11,
  color: 'var(--kind-failure)',
};

const secondaryStyle: React.CSSProperties = {
  padding: '7px 14px',
  fontSize: 12,
  fontWeight: 500,
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const dangerStyle: React.CSSProperties = {
  padding: '7px 12px',
  fontSize: 12,
  fontWeight: 500,
  background: 'transparent',
  color: 'var(--kind-failure)',
  border: '1px solid rgba(192,57,43,0.3)',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
